/**
 * What the buttons on an assistant clip card actually do.
 *
 * These are DIRECT USER ACTIONS, not agent edits: the user clicked them, so
 * they take effect immediately under the normal local origin with normal undo.
 * Nothing here goes through a proposal — the proposal gate exists for things
 * the assistant decided, and this is the user deciding (docs/agent.md §2).
 *
 * A card carries only archive/item/in/out, so placing one on the timeline needs
 * the item's per-part durations first: `in`/`out` are CONCAT seconds spanning
 * the whole item, while a clip addresses a single part. That is one `getItem`
 * per click, which is cheap and keeps the card payload (and the CRDT) small.
 */
import { archiveByKey, type Archive } from '../../api/archives';
import { getItem } from '../../api/pandora';
import { mediaUrl } from '../../engine/mediaurls';
import type { ClipCard } from '../../crdt/chat';
import { concatRangeToPartRange, MIN_PLACED_DURATION } from '../../format/partRange';
import type { PandoraVideoSource } from '../../format/types';
import { saveToBin } from '../../state/bin';
import { usePlayback } from '../../state/playback';
import { useTimeline } from '../../state/timeline';

export type ClipActionResult = { ok: true } | { ok: false; error: string };

/** Everything needed to play a card's range inline. */
export interface ClipMedia {
  src: string;
  /** Part-local seconds — what a <video> element's currentTime speaks. */
  inPoint: number;
  outPoint: number;
}

/**
 * A moment card can be a single frame — `machinedescriptions` rows on both
 * archives can be ~0.04s — and the timeline will not hold a clip shorter than
 * MIN_PLACED_DURATION. Widen the tail rather than failing the click; the user
 * asked for this frame and can drag the clip out from there.
 */
function playableOut(card: ClipCard): number {
  return card.out - card.in < MIN_PLACED_DURATION ? card.in + MIN_PLACED_DURATION : card.out;
}

interface Resolved {
  archive: Archive;
  title: string;
  totalDuration: number;
  durations: number[];
  parts: number;
  posterFrame: number | null;
}

async function resolve(card: ClipCard, archives: Archive[]): Promise<Resolved | string> {
  const archive = archiveByKey(archives, card.archive);
  if (!archive) return `unknown archive "${card.archive}"`;
  try {
    const detail = await getItem(archive.site, card.item);
    const durations = detail.durations ?? [];
    return {
      archive,
      title: detail.title || card.title || card.item,
      totalDuration: detail.duration ?? durations.reduce((a, b) => a + b, 0),
      durations,
      parts: detail.parts ?? durations.length ?? 1,
      posterFrame: detail.posterFrame,
    };
  } catch (err) {
    return `could not load ${card.item}: ${err instanceof Error ? err.message : String(err)}`;
  }
}

/**
 * Resolve a card to a playable stream plus the part-local range.
 *
 * `in`/`out` on a card are CONCAT seconds across the whole item, but media
 * files are per part, so a `<video>` needs both the right part's URL and the
 * range translated into that part's own clock.
 *
 * Lowest resolution on purpose: this is a thumbnail-sized preview in a chat
 * panel, and pulling the 480p stream to draw it 200px wide is wasteful on an
 * archive someone else is paying to host.
 */
export async function resolveClipMedia(
  card: ClipCard,
  archives: Archive[],
): Promise<ClipMedia | string> {
  const info = await resolve(card, archives);
  if (typeof info === 'string') return info;
  const range = concatRangeToPartRange(info.durations, card.in, playableOut(card));
  return {
    src: mediaUrl(info.archive, card.item, range.part, {
      resolution: Math.min(...info.archive.resolutions),
    }),
    inPoint: range.inPoint,
    outPoint: range.outPoint,
  };
}

/** Add the card's range to the timeline at the playhead. */
export async function addClipCardToTimeline(
  card: ClipCard,
  archives: Archive[],
): Promise<ClipActionResult> {
  const info = await resolve(card, archives);
  if (typeof info === 'string') return { ok: false, error: info };

  const range = concatRangeToPartRange(info.durations, card.in, playableOut(card));
  const source: PandoraVideoSource = {
    kind: 'pandora-video',
    archive: card.archive,
    item: card.item,
    part: range.part,
    duration: info.durations[range.part - 1] ?? info.totalDuration,
    title: info.title,
    posterFrame: info.posterFrame ?? 0,
    url: null,
  };
  useTimeline
    .getState()
    .addVideoClip(source, range.inPoint, range.outPoint, usePlayback.getState().currentTime);
  return { ok: true };
}

/** Save the card to the working bin, marks included. */
export async function addClipCardToBin(
  card: ClipCard,
  archives: Archive[],
): Promise<ClipActionResult> {
  const info = await resolve(card, archives);
  if (typeof info === 'string') return { ok: false, error: info };

  saveToBin({
    archive: card.archive,
    item: card.item,
    title: info.title,
    duration: info.totalDuration,
    parts: info.parts,
    ...(info.posterFrame != null ? { posterFrame: info.posterFrame } : {}),
    inPoint: card.in,
    outPoint: playableOut(card),
    ...(card.note ? { note: card.note } : {}),
  });
  return { ok: true };
}
